import { Inject, Injectable } from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { cloneDeep } from 'lodash';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { MeasurementToolsDetailsModel } from '../../models/low-level/measurement-tools-details.model';
import { TextModel } from '../../models/primitive/text.model';
import { UserGidService } from '../user-gid/user-gid.service';
import { OdataMeasurementToolsService } from './odata-measurement-tools.service';
import { MeasurementToolsInterface } from '../../interfaces/measurement-tools.interface';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { sortByGid } from '../../utilities/measurement-tools.utils';

@Injectable({
    providedIn: 'root',
})
export class MeasurementToolsService implements MeasurementToolsInterface, InitializableInterface {
    private measurementTools: MeasurementToolsDetailsModel[] = [];
    private dropdownEntries: Array<DisplayDecorator<TextModel>> = [];
    private originalDropdownEntries: Array<DisplayDecorator<TextModel>> = [];
    public isInitialized = false;
    public constructor(
        private readonly odataMeasurementToolsService: OdataMeasurementToolsService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly userGidService: UserGidService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public init(): Observable<MeasurementToolsDetailsModel[]> {
        if (!this.configService.getShowMeasurementToolsDetails()) {
            this.isInitialized = true;
            return of([]);
        }
        return this.odataMeasurementToolsService.getMeasurementTools().pipe(
            tap((measurementTools) => {
                this.measurementTools = measurementTools.sort((detailsModel) =>
                    detailsModel.gid === this.userGidService.getUserGid() ? -1 : 0,
                );
                this.updateDropdownEntries();
                this.isInitialized = true;
            }),
        );
    }
    public getMeasurementToolsDetailsList(): MeasurementToolsDetailsModel[] {
        return this.measurementTools;
    }

    private updateDropdownEntries(): void {
        this.originalDropdownEntries = this.measurementTools
            .filter((t) => t.calibratedDate.isSameOrAfter())
            .map((tool) => {
                const displayName = `${tool.descriptionDropdownText} - ${tool.technicalId} - ${tool.gid}`;
                return new DisplayDecorator<TextModel>(displayName, new TextModel('TechIdentNo', tool.technicalId));
            });
        this.log.debug(
            `MeasurementToolsService: updateDropdownEntries: Original dropdown entries ${this.originalDropdownEntries.length}`,
        );
        this.setDropdownEntries(this.originalDropdownEntries);
    }

    public getOriginalDropdownEntries(): Array<DisplayDecorator<TextModel>> {
        return this.originalDropdownEntries;
    }

    public setDropdownEntries(entries: Array<DisplayDecorator<TextModel>>): void {
        this.dropdownEntries = cloneDeep(this.sortByGid(entries));
        this.log.debug(
            `MeasurementToolsService: setDropdownEntries: Total unselected entries ${this.dropdownEntries.length}`,
        );
    }

    public sortByGid(entries: Array<DisplayDecorator<TextModel>>): Array<DisplayDecorator<TextModel>> {
        const userGid = this.userGidService.getUserGid();
        return sortByGid(entries, userGid);
    }
}
